import styled from 'styled-components';
import { extractApplicationAbr, extractSwtSize } from 'shared/helpers/helpers';

const SwitchDetailHeader = ({
  machineName,
  heatingSystem,
  locationName,
  isOff,
  isFaults,
  isActive,
  onClose,
  onViewAndPrint,
}) => {
  const statusLabel = isFaults ? 'FAULT' : isOff ? 'OFF' : isActive ? 'ACTIVE' : 'STANDBY';
  const statusColor = isFaults ? '#EE4B56' : isOff ? '#f97316' : isActive ? '#00D492' : 'var(--switch-text-faint)';
  const appAbr = extractApplicationAbr(heatingSystem);
  const swtSize = extractSwtSize(heatingSystem);
  const sizeLabel = Array.isArray(swtSize) ? swtSize.join('') : swtSize;
  const enrichedTitle = heatingSystem
    ? `${machineName || '---'}${sizeLabel ? ` #${sizeLabel}` : ''}${appAbr ? ` ${appAbr}.` : ''}`
    : machineName || '---';

  return (
    <Header>
      <HeaderLeft>
        <BackBtn onClick={onClose} title="Back to switches">
          <BackSvg viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </BackSvg>
        </BackBtn>

        <SwitchIdBadge>
          <SwitchIdDot $color={statusColor} />
        </SwitchIdBadge>

        <TitleGroup>
          <SwitchTitle>{enrichedTitle}</SwitchTitle>
          {locationName && <LocationSubtitle>{locationName}</LocationSubtitle>}
        </TitleGroup>

        <StatusBadge $color={statusColor}>{statusLabel}</StatusBadge>
      </HeaderLeft>

      <HeaderRight>
        <ActionBtn type="button">View Audit Trail</ActionBtn>
        <ActionBtn type="button" onClick={onViewAndPrint}>
          View &amp; Print
        </ActionBtn>
      </HeaderRight>
    </Header>
  );
};

export default SwitchDetailHeader;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid var(--switch-border);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-panel-bg);
  color: var(--switch-text-muted);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--switch-button-hover-bg);
    color: var(--switch-text-primary);
  }
`;

const BackSvg = styled.svg`
  width: 16px;
  height: 16px;
`;

const SwitchIdBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: rgba(0, 188, 125, 0.08);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #00D492;
  white-space: nowrap;
`;

const SwitchIdDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const SwitchTitle = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--switch-text-primary);
  letter-spacing: -0.3px;
  white-space: nowrap;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LocationSubtitle = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: var(--switch-text-faint);
  letter-spacing: 0.3px;
`;

const StatusBadge = styled.span`
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}18`};
  border: 1px solid ${({ $color }) => `${$color}30`};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionBtn = styled.button`
  padding: 6px 14px;
  border-radius: 10px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-button-bg);
  color: var(--switch-text-muted);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: var(--switch-shadow-soft);

  &:hover {
    color: var(--switch-text-primary);
    border-color: rgba(59, 130, 246, 0.4);
  }
`;
